import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { errMsg } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(identifier, password, "partner");
      navigate("/agent");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-[#F2F6FB]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {/* Brand panel */}
      <div className="hidden flex-1 flex-col justify-between p-12 lg:flex"
        style={{ background: "linear-gradient(135deg, #0F2A5C 0%, #1D5BD8 55%, #2E7BE6 100%)" }}>
        <div className="h-1.5 w-40 rounded-full" style={{ background: "linear-gradient(90deg,#FF9933,#FFFFFF,#138808)" }} />
        <div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="w-fit rounded-3xl bg-white p-4 shadow-2xl">
            <img src="/logo.png" alt="DropPartner" className="h-24 w-auto object-contain" />
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="mt-8 max-w-lg text-4xl font-extrabold leading-tight tracking-tight text-white xl:text-5xl"
            style={{ fontFamily: "Outfit, sans-serif" }}>
            One Product. <span className="text-orange-300">Multiple Channels.</span> One Powerful Dashboard.
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="mt-4 max-w-md text-sm leading-relaxed text-blue-100">
            Master catalog, agents, own stores, Amazon & Flipkart, unified orders, IndiaFast shipping, and accounting — all in one operating system.
          </motion.p>
        </div>
        <p className="text-xs text-blue-200">Source Global · Sell Local · Grow Together</p>
      </div>

      {/* Form */}
      <div className="flex w-full items-center justify-center px-6 lg:w-[480px]">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <div className="mb-8 flex justify-center lg:hidden">
            <img src="/logo.png" alt="DropPartner" className="h-20 w-auto rounded-2xl bg-white object-contain p-2 shadow-lg" />
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-[#0F2440]" style={{ fontFamily: "Outfit, sans-serif" }}>Partner sign in</h2>
          <p className="mt-1 text-sm text-slate-500">Access your DropPartner dashboard</p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-500">
                Email or Mobile
              </label>
              <Input data-testid="login-email-input" value={identifier} onChange={(e) => setIdentifier(e.target.value)}
                placeholder="you@example.com or 98XXXXXXXX"
                className="border-slate-200 bg-white text-[#0F2440] shadow-sm focus-visible:ring-[#FF6B00]" required />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-500">Password</label>
              <div className="relative">
                <Input data-testid="login-password-input" type={show ? "text" : "password"} value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                  className="border-slate-200 bg-white pr-10 text-[#0F2440] shadow-sm focus-visible:ring-[#FF6B00]" required />
                <button type="button" data-testid="login-toggle-password" onClick={() => setShow(!show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#1D5BD8]">
                  {show ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-xs text-slate-500">
                <Checkbox data-testid="login-remember" checked={remember} onCheckedChange={setRemember} /> Remember me
              </label>
              <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs font-medium text-[#1D5BD8] hover:text-[#FF6B00]">
                Forgot password?
              </Link>
            </div>
            {error && <p data-testid="login-error" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-600">{error}</p>}<Button data-testid="login-submit-btn" disabled={busy}
              className="w-full bg-[#FF6B00] font-semibold text-white shadow-md transition-all hover:bg-[#E85D04] hover:shadow-lg">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <p className="mt-4 text-center text-xs text-slate-500">
            New seller? <Link to="/register" data-testid="login-register-link" className="font-semibold text-[#1D5BD8] hover:text-[#FF6B00]">Create your account</Link>
          </p>
          <p className="mt-6 text-center text-[11px] text-slate-400">
            Customer? Visit your agent's store link to shop — no account needed.
          </p>
          <Link to="/" data-testid="login-back-home" className="mt-2 block text-center text-xs font-medium text-[#1D5BD8] hover:text-[#FF6B00]">
            ← Back to home
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
